/*
 * Team (U6) — brand-admin member management (full CRUD).
 *
 * In the admin-provisioned model there is no self-registration: a brand admin
 * creates each user with an INITIAL password and a forced first-login change.
 * This screen now also LISTS the brand's members (`GET /auth/members`) and lets
 * the admin change a member's role, disable/enable their membership
 * (`PATCH /auth/members/{user_id}`), and remove it (`DELETE`). The backend guards
 * against lockout — disabling/demoting/removing the last enabled admin, or an
 * admin locking themselves out — and answers `409`; we surface that as a
 * disabled control + inline note, and a `404` (no longer a member) plainly.
 *
 * Gated to admin (`can("administer")`) — operators/viewers see a permission note.
 */

import { useMemo, useState, type FormEvent } from "react";
import {
  Box,
  Group,
  NativeSelect,
  Paper,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import {
  Alert,
  Button,
  PageHeader,
  Table,
  type Column,
} from "../../components/ui";
import { StatusPill } from "../../components/StatusPill";
import { Section } from "../../components/config/Section";
import { useAuth } from "../../auth/AuthContext";
import { ApiError } from "../../api/client";
import { errorMessage } from "../../api/errors";
import type { Role, MemberRosterEntry } from "../../api/client";
import {
  useMembers,
  useCreateUser,
  useUpdateMember,
  useRemoveMember,
} from "./hooks";

const DESCRIPTION =
  "Manage the members of this brand — provision new users, change roles, disable access, or remove a member.";

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: "admin", label: "Admin — manage members + everything below" },
  { value: "operator", label: "Operator — launch + control runs" },
  { value: "viewer", label: "Viewer — read-only" },
];

const ROLE_LABEL: Record<Role, string> = {
  admin: "Admin",
  operator: "Operator",
  viewer: "Viewer",
};

interface Provisioned {
  email: string;
  role: Role;
  password: string;
}

export function TeamPage() {
  const { can, user } = useAuth();
  const canAdmin = can("administer");

  const membersQuery = useMembers({ enabled: canAdmin });
  const create = useCreateUser();
  const update = useUpdateMember();
  const remove = useRemoveMember();

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("operator");
  const [password, setPassword] = useState("");
  const [provisioned, setProvisioned] = useState<Provisioned | null>(null);
  const [copied, setCopied] = useState(false);

  const members = membersQuery.data ?? [];

  // The only enabled admin is the last admin — its disable/demote/remove
  // controls must be locked (the backend would 409 anyway).
  const enabledAdmins = useMemo(
    () => members.filter((m) => m.role === "admin" && !m.disabled),
    [members],
  );

  if (!canAdmin) {
    return (
      <>
        <PageHeader title="Team" description={DESCRIPTION} />
        <Alert tone="info">
          Only a brand admin can manage members. Ask an admin to provision your
          teammates.
        </Alert>
      </>
    );
  }

  // A 409 means the email is already a member of this brand — surface it plainly.
  const conflict =
    create.error instanceof ApiError && create.error.status === 409;
  const createError = create.isError
    ? conflict
      ? "That email is already a member of this brand."
      : errorMessage(create.error)
    : null;

  // Surface a member-mutation failure (guard 409 / 404 / 403) above the roster.
  const mutateError = memberMutationError(update.error ?? remove.error);

  function resetForm() {
    setEmail("");
    setRole("operator");
    setPassword("");
    create.reset();
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    const payload = { email: email.trim(), role, password };
    create.mutate(payload, {
      onSuccess: () => {
        setProvisioned({ email: payload.email, role: payload.role, password });
        setCopied(false);
        resetForm();
      },
    });
  }

  async function copyPassword() {
    if (!provisioned) return;
    try {
      await navigator.clipboard.writeText(provisioned.password);
      setCopied(true);
    } catch {
      // Clipboard unavailable — the value is selectable in the field regardless.
      setCopied(false);
    }
  }

  /** Is this member the LAST enabled admin of the brand? (lockout guard) */
  function isLastAdmin(m: MemberRosterEntry): boolean {
    return (
      m.role === "admin" &&
      !m.disabled &&
      enabledAdmins.length <= 1 &&
      enabledAdmins.some((a) => a.user_id === m.user_id)
    );
  }

  const pendingUserId = update.isPending
    ? update.variables?.userId
    : remove.isPending
      ? remove.variables
      : null;

  const columns: Column<MemberRosterEntry>[] = [
    {
      key: "email",
      header: "Member",
      render: (m) => (
        <span>
          {m.email}
          {m.user_id === user?.id && (
            <Text component="span" c="dimmed" fz="sm">
              {" "}
              (you)
            </Text>
          )}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (m) =>
        m.disabled ? (
          <StatusPill tone="danger" label="Disabled" />
        ) : (
          <StatusPill tone="success" label="Active" />
        ),
    },
    {
      key: "actions",
      header: "Role & actions",
      align: "right",
      render: (m) => {
        const lastAdmin = isLastAdmin(m);
        const isSelf = m.user_id === user?.id;
        const busy = pendingUserId === m.user_id;
        // Lock the controls that would lock the brand out: the last admin can't
        // be demoted/disabled/removed, and an admin can't disable/remove itself.
        const lockChange = busy || lastAdmin;
        const lockDestructive = busy || lastAdmin || isSelf;

        return (
          <Group gap="xs" justify="flex-end" wrap="wrap">
            <NativeSelect
              w="9rem"
              aria-label={`Role for ${m.email}`}
              value={m.role}
              disabled={lockChange}
              onChange={(e) =>
                update.mutate({
                  userId: m.user_id,
                  body: { role: e.currentTarget.value as Role },
                })
              }
              data={ROLE_OPTIONS.map((o) => ({
                value: o.value,
                label: ROLE_LABEL[o.value],
              }))}
            />
            <Button
              variant="secondary"
              size="sm"
              disabled={m.disabled ? busy : lockDestructive}
              onClick={() =>
                update.mutate({
                  userId: m.user_id,
                  body: { disabled: !m.disabled },
                })
              }
            >
              {m.disabled ? "Enable" : "Disable"}
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={lockDestructive}
              loading={remove.isPending && remove.variables === m.user_id}
              onClick={() => {
                if (
                  window.confirm(
                    `Remove ${m.email} from this brand? They keep their account and any other brands.`,
                  )
                ) {
                  remove.mutate(m.user_id);
                }
              }}
            >
              Remove
            </Button>
          </Group>
        );
      },
    },
  ];

  const hasLastAdmin = members.some((m) => isLastAdmin(m));

  return (
    <>
      <PageHeader title="Team" description={DESCRIPTION} />

      {provisioned && (
        <Box maw={520} mb="lg">
          <Paper withBorder radius="md" p="md" bg="var(--mantine-color-default-hover)">
            <Stack gap="sm">
              <Text fw={600} fz="sm">
                Share this password with {provisioned.email}
              </Text>
              <Text fz="sm" c="dimmed">
                They will sign in as a <strong>{provisioned.role}</strong> using
                this password. This is the only time it is shown.
              </Text>
              <Group gap="sm" wrap="nowrap">
                <TextInput
                  flex={1}
                  readOnly
                  value={provisioned.password}
                  aria-label="Provisioned password"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button variant="secondary" size="sm" onClick={copyPassword}>
                  {copied ? "Copied" : "Copy"}
                </Button>
              </Group>
              <Group gap="sm">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setProvisioned(null)}
                >
                  Dismiss
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Box>
      )}

      <Stack gap="xl">
        <Section
          title="Members"
          description="Everyone with access to this brand. Change a role, disable access, or remove a member."
        >
          {mutateError && <Alert tone="error">{mutateError}</Alert>}

          {membersQuery.isError ? (
            <Alert tone="error">{errorMessage(membersQuery.error)}</Alert>
          ) : membersQuery.isLoading ? (
            <Text c="dimmed" fz="sm">
              Loading members…
            </Text>
          ) : (
            <>
              <Table
                columns={columns}
                rows={members}
                rowKey={(m) => m.user_id}
                empty="No members yet."
              />
              {hasLastAdmin && (
                <Text c="dimmed" fz="xs" mt="xs">
                  The last admin of a brand can't be demoted, disabled, or
                  removed — promote another member to admin first.
                </Text>
              )}
            </>
          )}
        </Section>

        <Box maw={520}>
          <Section
            title="Add a member"
            description="Create a user in this brand and choose their role."
          >
            <form onSubmit={onSubmit} noValidate>
              <Stack gap="md">
                {createError && <Alert tone="error">{createError}</Alert>}
                <TextInput
                  label="Email"
                  required
                  type="email"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  placeholder="teammate@company.com"
                />
                <NativeSelect
                  label="Role"
                  required
                  value={role}
                  onChange={(e) => setRole(e.currentTarget.value as Role)}
                  data={ROLE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
                <TextInput
                  label="Password"
                  required
                  type="text"
                  autoComplete="off"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  placeholder="A strong initial password"
                  minLength={8}
                  description="The user can change this later from their account settings."
                />
                <Group>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={create.isPending}
                    disabled={!email.trim() || !password}
                  >
                    Create user
                  </Button>
                </Group>
              </Stack>
            </form>
          </Section>
        </Box>
      </Stack>
    </>
  );
}

/**
 * Turn a member-mutation error into an inline message. The backend guards
 * lockout (409) and answers 404 for a member no longer in this brand; anything
 * else falls back to the shared formatter.
 */
function memberMutationError(err: unknown): string | null {
  if (err == null) return null;
  if (err instanceof ApiError) {
    if (err.status === 409) {
      return (
        errorMessageDetail(err) ??
        "That change would leave the brand without an admin, or lock you out."
      );
    }
    if (err.status === 404) {
      return "That member is no longer part of this brand.";
    }
  }
  return errorMessage(err);
}

/** Pull a server `detail` string out of an ApiError body, if present. */
function errorMessageDetail(err: ApiError): string | null {
  const body = err.body;
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return null;
}

export default TeamPage;
